import { useId } from "react";
import type * as api from "./api";
import { calendarDays, clipTop, conePaths, conePointsPath, dateStart, daysFrom, isWeekend, labelMondays, labelSide, tickAnchor } from "./chart";
import { type Cross, crossAt, crossDate, type CrossLines, dateAt, historyDays, lineAt, medianLine, pickupRamps, timeAxis, type View } from "./coneView";
import { fillWords } from "./fill";
import { dateLabel } from "./format";
import { useMedia } from "./Menu";

export const CONE_TEXT = "The fill will be inside the inner band 68% of the time, and inside the outer band 95% of the time.";

const NARROW = "(max-width: 40rem)";
const TOP_SHARE = 1.4;
export const WEEKEND_TEXT = "Shaded columns are Saturdays and Sundays.";
export const OVER_TEXT = `over ${Math.round(TOP_SHARE * 100)}%`;

/** A visit line in place of the next visit, for example the day of a route plan: "Planned Mon Oct 26". */
export interface ConeVisit {
  date: string;
  label: string;
}

/** The size and the visible days of the zoomable page chart; with no layout the chart shows all its days. */
export interface ConeLayout {
  view: View;
  width: number;
  height: number;
}

interface Frame {
  width: number;
  height: number;
  left: number;
  top: number;
  bottom: number;
  yTicks: number[];
  mondayEvery: number;
  weekdays: boolean;
}

const CARD: Frame = { width: 320, height: 202, left: 38, top: 8, bottom: 180, yTicks: [0, 0.5, 1, 1.4], mondayEvery: 2, weekdays: false };
const PAGE: Frame = { width: 720, height: 300, left: 44, top: 10, bottom: 264, yTicks: [0, 0.2, 0.4, 0.6, 0.8, 1, 1.2, 1.4], mondayEvery: 1, weekdays: true };
const NARROW_PAGE: Frame = { ...PAGE, width: 360, height: 260, bottom: 224, yTicks: [0, 0.5, 1, 1.4], mondayEvery: 2 };
const NARROW_PX = 560;
const AXIS_ROOM = 36;

/** The frame of the zoomable chart at its real pixel size. */
export function layoutFrame(width: number, height: number): Frame {
  const base = width < NARROW_PX ? NARROW_PAGE : PAGE;
  return { ...base, width, height, bottom: height - AXIS_ROOM, yTicks: height >= 360 ? PAGE.yTicks : base.yTicks };
}
const DAY_TICK = 4;
const CHAR_WIDTH = 7;
const TODAY_WIDTH = 5 * CHAR_WIDTH;

/** The labels of the visit line, longest first: "Next visit Mon Nov 2", "Next visit Nov 2", "Visit Nov 2". With a fill, each first says "@ 60%". */
export function visitLabels(date: string, override?: ConeVisit, fill?: number | null): string[] {
  const plain = override
    ? [`${override.label} ${dateLabel(date, "both")}`, `${override.label} ${dateLabel(date)}`]
    : [`Next visit ${dateLabel(date, "both")}`, `Next visit ${dateLabel(date)}`, `Visit ${dateLabel(date)}`];
  if (fill == null) return plain;
  return [...plain.map((text) => `${text} @ ${Math.round(fill * 100)}%`), ...plain];
}

export const RAMP_TEXT = "Estimated between pickups";
export const NO_HISTORY_TEXT = "No pickup or reading yet. The past is estimated back from the stated rate.";

/** The key words of the back-cast (SO-4291): from what the estimate knows first. */
export function backcastText(anchor: api.FillCone["anchor"]): string {
  if (!anchor?.at || anchor.type === "none" || anchor.type === "start") return "Estimated back from the stated rate";
  return anchor.type === "empty" ? "Estimated back from the last pickup" : "Estimated back from the last reading";
}

const MINUTE_DAYS = 1 / 1440;

/** A pickup before the anchor empties the container. Thus the back-cast starts at 0 at that pickup and goes up to the anchor. */
export function fromLastPickup(back: api.ConeDay[], cone: Pick<api.FillCone, "anchor" | "history">, now: number): api.ConeDay[] {
  const end = back[back.length - 1];
  if (!end || end.p50 == null || !cone.anchor?.at) return back;
  const anchor = daysFrom(cone.anchor.at, now);
  const pickups = (cone.history ?? []).filter((point) => point.kind === "pickup").map((point) => daysFrom(point.at, now));
  const from = Math.max(...pickups.filter((day) => day < anchor - MINUTE_DAYS));
  if (!Number.isFinite(from) || from >= end.day) return back;
  const top = end.p50;
  const rise = (day: number): api.ConeDay => {
    const share = Math.round(((top * (day - from)) / (end.day - from)) * 1000) / 1000;
    return { day, p2: share, p16: share, p50: share, p84: share, p97: share };
  };
  return [rise(from), ...back.filter((day) => day.day > from).map((day) => rise(day.day))];
}

/** The back-cast days; with no anchor, it joins the band of today, so the dashed line meets it. */
export function backcastDays(cone: Pick<api.FillCone, "anchor" | "days" | "backcast" | "history">, now = Date.now()): api.ConeDay[] {
  const back = fromLastPickup(cone.backcast ?? [], cone, now);
  if (!back.length) return [];
  const first = cone.days.find((day) => day.p50 != null && day.day > back[back.length - 1].day);
  const joins = first && first.day - back[back.length - 1].day <= 1 && cone.anchor?.type !== "empty";
  return joins ? [...back, first] : back;
}

/** The lines that the crosshair reads: the median, the sensor readings, the back-cast and the lines between pickups. */
export function crossLines(cone: api.FillCone, now: number): CrossLines {
  const history = historyDays(cone, now);
  const back = backcastDays(cone, now);
  const median = medianLine(cone.days);
  const ramps = pickupRamps(history, back[0]?.day ?? median[0]?.day ?? 0);
  const readings = history.filter((point) => point.kind === "level").map((point) => ({ day: point.day, share: point.share }));
  return { median, readings, back: [medianLine(back), ...ramps] };
}

interface CrosshairProps {
  at: number;
  cross: Cross | null;
  y: (share: number) => number;
  left: number;
  right: number;
  top: number;
  bottom: number;
  date: string;
}

/** The crosshair of the page chart: a vertical line at the cursor, and a horizontal line from the Y axis to the fill line. */
function Crosshair({ at, cross, y, left, right, top, bottom, date }: CrosshairProps) {
  const cy = cross ? y(Math.min(Math.max(cross.share, 0), TOP_SHARE)) : null;
  const value = cross ? `${Math.round(cross.share * 100)}%` : "";
  const after = at + 8 + value.length * CHAR_WIDTH <= right;
  const dateWidth = date.length * CHAR_WIDTH + 12;
  const dateX = Math.min(Math.max(at - dateWidth / 2, left - 8), right + 8 - dateWidth);
  return (
    <g className="cone-cross" data-testid="cone-cross" pointerEvents="none">
      <line x1={at} y1={top} x2={at} y2={bottom} className="cone-cross-line" />
      {cy !== null && (
        <>
          <line x1={left} y1={cy} x2={at} y2={cy} className="cone-cross-line" data-testid="cone-cross-h" />
          <circle cx={at} cy={cy} r={4.5} className="cone-cross-dot" />
          <text x={after ? at + 8 : at - 8} y={cy - 8 < top + 12 ? cy + 18 : cy - 8} textAnchor={after ? "start" : "end"} className="cone-label cone-cross-value" data-testid="cone-cross-value">
            {value}
          </text>
        </>
      )}
      <rect x={dateX} y={bottom + 3} width={dateWidth} height={19} rx={4} className="cone-cross-date-box" />
      <text x={dateX + dateWidth / 2} y={bottom + 17} textAnchor="middle" className="cone-cross-date" data-testid="cone-cross-date">
        {date}
      </text>
    </g>
  );
}

const Over = ({ x, y }: { x: number; y: number }) => <path d={`M${x.toFixed(1)} ${(y - 1).toFixed(1)}l-4.5 7h9z`} className="cone-over" data-testid="cone-over" />;

/** The day of the crosshair and the drag selection of the zoomable chart. */
export interface ConeMarks {
  cursor?: number | null;
  selection?: View | null;
}

/** The projection cone of rule U-24: the 95% and 68% bands, the median, and the pickups and readings of the past days. */
export function FillCone({
  cone,
  size,
  now = Date.now(),
  visit: override,
  layout,
  marks,
}: {
  cone: api.FillCone;
  size: "card" | "page";
  now?: number;
  visit?: ConeVisit;
  layout?: ConeLayout;
  marks?: ConeMarks;
}) {
  const narrow = useMedia(NARROW);
  const clip = useId();
  const frame = layout ? layoutFrame(layout.width, layout.height) : size === "card" ? CARD : narrow ? NARROW_PAGE : PAGE;
  const { width, height, left, top, bottom } = frame;
  const right = width - 10;
  const first = layout?.view.from ?? cone.days[0]?.day ?? -28;
  const last = layout?.view.to ?? cone.days[cone.days.length - 1]?.day ?? 28;
  const inView = (day: number) => day >= first - MINUTE_DAYS && day <= last + MINUTE_DAYS;
  const noHistory = !cone.anchor?.at || cone.anchor.type === "none";
  const back = backcastDays(cone, now);
  const x = layout
    ? (day: number) => left + ((day - first) / Math.max(MINUTE_DAYS, last - first)) * (right - left)
    : (day: number) => left + ((Math.min(Math.max(day, first), last) - first) / Math.max(1, last - first)) * (right - left);
  const y = (share: number) => bottom - (Math.max(share, 0) / TOP_SHARE) * (bottom - top);
  const paths = conePaths(cone.days, x, y, TOP_SHARE);
  const backPaths = conePaths(back, x, y, TOP_SHARE);
  const history = layout ? historyDays(cone, now) : (cone.history ?? []).map((point) => ({ ...point, day: daysFrom(point.at, now) })).filter((point) => point.day >= first && point.day <= 0);
  const levelPoints = history.filter((point) => point.kind === "level");
  const levels = clipTop(levelPoints, TOP_SHARE);
  const median = medianLine(cone.days);
  const ramps = layout ? pickupRamps(history, back[0]?.day ?? median[0]?.day ?? 0) : [];
  const rampPaths = ramps.map((ramp) => clipTop(ramp, TOP_SHARE));
  const visitDate = override?.date ?? cone.next_visit ?? null;
  const visit = visitDate ? dateStart(visitDate, now) : null;
  const showVisit = visitDate !== null && visit !== null && visit >= (override ? first : layout ? Math.max(first, 0) : 0) && visit <= last;
  const visitAt = visit === null ? 0 : x(visit);
  const visitFill = layout && visit !== null ? lineAt(median, visit) : null;
  const sides = showVisit ? visitLabels(visitDate, override, visitFill).map((text) => ({ text, side: labelSide(visitAt, text.length * CHAR_WIDTH, left, right) })) : [];
  const clearOfToday = (text: string) => visitAt - 4 - text.length * CHAR_WIDTH > x(0) + 8 + TODAY_WIDTH;
  const clearOfLine = (text: string) => visitAt <= x(0) || visitAt - 4 - text.length * CHAR_WIDTH > x(0) + 4;
  const leftFits = (text: string) => visitAt - 4 - text.length * CHAR_WIDTH >= left;
  const past = visitAt < x(0) ? sides.find((label) => leftFits(label.text)) : undefined;
  // A past day goes left of its line. Otherwise right of it, then left clear of Today; else the key.
  const fitted =
    (past && { text: past.text, side: "end" as const }) ??
    (visitAt < x(0) ? undefined : sides.find((label) => label.side === "start")) ??
    sides.find((label) => label.side === "end" && clearOfToday(label.text)) ??
    sides.filter((label) => label.side === "end" && clearOfLine(label.text)).slice(-1)[0];
  const visitLabel = { text: fitted?.text ?? "", side: fitted?.side ?? "end" };
  const visitWidth = visitLabel.text.length * CHAR_WIDTH;
  const visitFrom = visitLabel.side === "end" ? visitAt - 4 - visitWidth : visitAt + 4;
  const showToday = !layout || inView(0);
  const todayLeft = showVisit && visitAt > x(0) && visitAt - x(0) < TODAY_WIDTH + 8;
  const todayFrom = todayLeft ? x(0) - 4 - TODAY_WIDTH : x(0) + 4;
  const visitOverToday = showToday && showVisit && fitted !== undefined && visitFrom < todayFrom + TODAY_WIDTH + 4 && todayFrom < visitFrom + visitWidth + 4;
  const axis = layout ? timeAxis(layout.view, now, right - left) : null;
  const calendar = !axis || axis.weekends ? calendarDays(first, last, now) : [];
  const days = calendar.filter((day) => day.start >= first && day.start <= last);
  const today = cone.days.find((day) => day.day === 0)?.p50 ?? null;
  const overText = paths.over.length > 0 ? ` The most likely fill goes ${OVER_TEXT}.` : "";
  const backOverText = backPaths.over.length > 0 ? ` The estimate back goes ${OVER_TEXT}.` : "";
  const backText = back.length ? ` ${backcastText(cone.anchor)} (dashed).` : "";
  const span = layout ? `from ${dateLabel(dateAt(first, now))} to ${dateLabel(dateAt(last, now))}` : `for ${-first} days before and ${last} days after today`;
  const summary = `Fill estimate ${span}. Today: ${fillWords(today).toLowerCase()}.${overText}${backText}${backOverText}${noHistory ? ` ${NO_HISTORY_TEXT}` : ""}`;
  const labelY = bottom - 5;
  const drawn = new Set(history.filter((point) => !layout || inView(point.day)).map((point) => point.kind));
  const overDay = [...paths.over, ...backPaths.over].find(inView);
  const overAt = overDay === undefined ? null : x(overDay);
  const overSide = overAt === null ? "start" : (labelSide(overAt + 4, OVER_TEXT.length * CHAR_WIDTH, left, right) ?? "end");
  const dataClip = layout ? `url(#${clip}-data)` : undefined;
  const cursor = layout && marks?.cursor != null && inView(marks.cursor) ? marks.cursor : null;
  const cross = cursor === null ? null : crossAt(cursor, crossLines(cone, now));
  const selection = layout && marks?.selection ? marks.selection : null;
  return (
    <figure className={`fill-cone ${size}${layout ? " zoom" : ""}`} data-testid={`fill-cone-${size}`}>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={summary}>
        <defs>
          <clipPath id={clip}>
            <rect x={left} y={top} width={right - left} height={bottom - top} />
          </clipPath>
          {layout && (
            <clipPath id={`${clip}-data`}>
              <rect x={left} y={top - 8} width={right - left} height={bottom - top + 12} />
            </clipPath>
          )}
        </defs>
        <g clipPath={dataClip}>
          {calendar.filter(isWeekend).map((day) => (
            <rect key={day.date} x={x(day.start)} y={top} width={Math.max(0, x(day.start + 1) - x(day.start))} height={bottom - top} className="cone-weekend" data-testid="cone-weekend" />
          ))}
        </g>
        {frame.yTicks.map((share) => (
          <g key={share}>
            <line x1={left} y1={y(share)} x2={right} y2={y(share)} className="chart-grid" />
            <text x={left - 5} y={y(share) + 4} className="chart-axis" textAnchor="end">
              {Math.round(share * 100)}%
            </text>
          </g>
        ))}
        <g clipPath={`url(#${clip})`}>
          <path d={paths.outer} className="cone-outer" />
          <path d={paths.inner} className="cone-inner" />
        </g>
        <line x1={left} y1={y(1)} x2={right} y2={y(1)} className="cone-full" />
        <text x={left + 4} y={y(1) - 4} className="cone-label cone-full-text">
          Full
        </text>
        {showToday && (
          <>
            <line x1={x(0)} y1={top} x2={x(0)} y2={bottom} className="cone-now" />
            <text x={todayLeft ? x(0) - 4 : x(0) + 4} y={labelY} textAnchor={todayLeft ? "end" : "start"} className="cone-label cone-now-text">
              Today
            </text>
          </>
        )}
        {showVisit && (
          <>
            <line x1={visitAt} y1={top} x2={visitAt} y2={bottom} className="cone-visit" data-testid="cone-visit" />
            {fitted && (
              <text
                x={visitAt + (visitLabel.side === "end" ? -4 : 4)}
                y={visitOverToday ? labelY - 15 : labelY}
                textAnchor={visitLabel.side}
                className="cone-label cone-visit-text"
                data-testid="cone-visit-text"
              >
                {visitLabel.text}
              </text>
            )}
            {visitFill !== null && visit !== null && <circle cx={visitAt} cy={y(Math.min(visitFill, TOP_SHARE))} r={4} className="cone-visit-dot" data-testid="cone-visit-dot" />}
          </>
        )}
        <g clipPath={dataClip}>
          {levels.runs.length > 0 && <path d={levels.runs.map((run) => conePointsPath(run, x, y)).join("")} className="cone-level" />}
          {levels.exits.map((day) => (
            <Over key={`level-${day}`} x={x(day)} y={top} />
          ))}
          {rampPaths.length > 0 && <path d={rampPaths.flatMap((part) => part.runs.map((run) => conePointsPath(run, x, y))).join("")} className="cone-back-median" data-testid="cone-ramp" />}
          {history.map((point) => {
            if (layout && !inView(point.day)) return null;
            const over = point.share > TOP_SHARE;
            if (point.kind === "pickup") {
              return (
                <g key={`${point.kind}-${point.at}`} data-kind="pickup">
                  <line x1={x(point.day)} y1={y(Math.min(point.share, TOP_SHARE))} x2={x(point.day)} y2={y(0)} className="cone-drop" />
                  {over ? <Over x={x(point.day)} y={top} /> : <circle cx={x(point.day)} cy={y(point.share)} r={3.5} className="cone-pickup" />}
                </g>
              );
            }
            if (point.kind === "level") return null;
            if (over) return <Over key={`${point.kind}-${point.at}`} x={x(point.day)} y={top} />;
            return <circle key={`${point.kind}-${point.at}`} cx={x(point.day)} cy={y(point.share)} r={3} className={`cone-${point.kind}`} data-kind={point.kind} />;
          })}
          <path d={paths.median} className="cone-median" />
          {back.length > 0 && <path d={backPaths.median} className="cone-back-median" data-testid="cone-backcast" />}
          {[...paths.over, ...backPaths.over].map((day, index) => (
            <Over key={`median-${index}-${day}`} x={x(day)} y={top} />
          ))}
        </g>
        {overAt !== null && (
          <text x={overAt + (overSide === "end" ? -8 : 8)} y={top + 12} textAnchor={overSide} className="cone-label cone-over-text" data-testid="cone-over-text">
            {OVER_TEXT}
          </text>
        )}
        {axis ? (
          <>
            {axis.minor.map((day) => (
              <line key={`minor-${day}`} x1={x(day)} y1={bottom} x2={x(day)} y2={bottom + DAY_TICK} className="cone-day-tick" />
            ))}
            {axis.major.map((day) => (
              <line key={`major-${day}`} x1={x(day)} y1={bottom} x2={x(day)} y2={bottom + DAY_TICK * 1.5} className="cone-day-tick" />
            ))}
            {axis.labels.map((tick) => {
              const anchor = tickAnchor(x(tick.day), left, right, 18);
              return (
                <text key={tick.date} x={x(tick.day)} y={bottom + 17} className="chart-axis" textAnchor={anchor} data-testid="cone-axis-label">
                  {tick.lines.map((line, index) => (
                    <tspan key={line} x={x(tick.day)} dy={index ? 14 : 0}>
                      {line}
                    </tspan>
                  ))}
                </text>
              );
            })}
          </>
        ) : (
          <>
            {days.map((day) => (
              <line key={day.date} x1={x(day.start)} y1={bottom} x2={x(day.start)} y2={bottom + (day.weekday === 1 ? DAY_TICK * 1.5 : DAY_TICK)} className="cone-day-tick" />
            ))}
            {labelMondays(days, frame.mondayEvery).map((day) => {
              const anchor = tickAnchor(x(day.start), left, right, 18);
              return (
                <text key={day.date} x={x(day.start)} y={bottom + 17} className="chart-axis" textAnchor={anchor} data-testid="cone-monday">
                  {frame.weekdays ? (
                    <>
                      <tspan x={x(day.start)}>{dateLabel(day.date, "weekday")}</tspan>
                      <tspan x={x(day.start)} dy={14}>
                        {dateLabel(day.date)}
                      </tspan>
                    </>
                  ) : (
                    dateLabel(day.date)
                  )}
                </text>
              );
            })}
          </>
        )}
        {selection && (
          <rect
            x={Math.min(x(selection.from), x(selection.to))}
            y={top}
            width={Math.abs(x(selection.to) - x(selection.from))}
            height={bottom - top}
            className="cone-select"
            data-testid="cone-select"
          />
        )}
        {cursor !== null && <Crosshair at={x(cursor)} cross={cross} y={y} left={left} right={right} top={top} bottom={bottom} date={crossDate(dateAt(cursor, now), now)} />}
      </svg>
      <figcaption className="chart-key">
        <span className="cone-key" data-testid="cone-key">
          <span className="key-cone-inner">{size === "page" ? "68% of the time" : "68%"}</span> <span className="key-cone-outer">{size === "page" ? "95% of the time" : "95%"}</span> <span className="key-cone-median">Most likely</span>
          {(back.length > 0 || rampPaths.length > 0) && (
            <>
              {" "}
              <span className="key-cone-back">{back.length > 0 ? backcastText(cone.anchor) : RAMP_TEXT}</span>
            </>
          )}
          {showVisit && (
            <>
              {" "}
              <span className="key-cone-visit">{fitted ? (override ? override.label : "Visit") : visitLabels(visitDate, override).slice(-1)[0]}</span>
            </>
          )}
          {drawn.has("pickup") && (
            <>
              {" "}
              <span className="key-cone-pickup">Pickup</span>
            </>
          )}
          {drawn.has("level") && (
            <>
              {" "}
              <span className="key-cone-level">Sensor readings</span>
            </>
          )}
          {drawn.has("float") && (
            <>
              {" "}
              <span className="key-cone-float">Float reports</span>
            </>
          )}
          {drawn.has("driver") && (
            <>
              {" "}
              <span className="key-cone-driver">Driver reports</span>
            </>
          )}
        </span>
        <br />
        {noHistory && (
          <span className="cone-note" data-testid="cone-no-history">
            {NO_HISTORY_TEXT}{" "}
          </span>
        )}
        {CONE_TEXT}
        {size === "page" && (!axis || axis.weekends) && ` ${WEEKEND_TEXT}`}
      </figcaption>
    </figure>
  );
}
